/**
 * useProductPricing — ViewModel for /products/pricing.
 *
 * Two-level UI surface:
 *   1. Paginated list of active variants with mapping summary + baseline cost.
 *   2. Per-variant detail (current links + picker catalogue) loaded on
 *      demand for the edit modal. PATCH replaces the entire mapping set.
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination". usePaginatedList
 * powers level 1.
 */

import { useCallback, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type {
  ProductVariantMaterialsDetailDto,
  ProductVariantMaterialsSummaryDto
} from '../models/product-variant-materials.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth.
export type {
  ProductVariantMaterialLink,
  ProductVariantMaterialsDetailDto,
  ProductVariantMaterialsSummaryDto,
  RawMaterialPickerEntry
} from '../models/product-variant-materials.model'

export interface SyncMaterialsInput {
  readonly materials: ReadonlyArray<{
    readonly raw_material_id: string
    readonly supplier_id: string
    readonly quantity_millis: number
  }>
}

/** A product option for the list's product filter. */
export interface ProductFilterOption {
  readonly id: string
  readonly name: string
}

/** Sentinel for the "All products" default. */
export const PRODUCTFILTERALL = 'all'

export interface UseProductPricingReturn {
  readonly items: ProductVariantMaterialsSummaryDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly fetchDetail: (
    variantId: string
  ) => Promise<{ ok: true; data: ProductVariantMaterialsDetailDto } | { ok: false; message: string }>
  readonly syncMaterials: (
    variantId: string,
    input: SyncMaterialsInput
  ) => Promise<{ ok: true; data: ProductVariantMaterialsDetailDto } | { ok: false; field: string | null; message: string }>
  /**
   * Set a BUY variant's typed baseline cost (written to preview; applied
   * on Accept). Server rejects this for Make variants.
   */
  readonly savePreviewCost: (
    variantId: string,
    previewCostPaisa: number
  ) => Promise<{ ok: true; data: ProductVariantMaterialsDetailDto } | { ok: false; field: string | null; message: string }>
  readonly acceptVariant: (
    variantId: string
  ) => Promise<{ ok: true } | { ok: false; message: string }>
  /**
   * Clone a source variant's materials + cost onto every other variant
   * of its product (overwrite). Returns the count cloned to.
   */
  readonly cloneMaterials: (
    sourceVariantId: string
  ) => Promise<{ ok: true; clonedCount: number } | { ok: false; message: string }>
  /**
   * All variants of one product (not paginated) — powers the clone
   * source dropdown.
   */
  readonly listProductVariants: (
    productId: string
  ) => Promise<{ ok: true; variants: ProductVariantMaterialsSummaryDto[] } | { ok: false; message: string }>
  /** Distinct products with ≥1 variant — options for the product filter. */
  readonly products: ProductFilterOption[]
  /** Current product filter (`PRODUCTFILTERALL` or a product id). */
  readonly productFilter: string
  readonly setProductFilter: (id: string) => void
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: ProductVariantMaterialsSummaryDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
    readonly products: ProductFilterOption[]
  }
}

interface DetailBody {
  readonly success: true
  readonly data: ProductVariantMaterialsDetailDto
}

export function useProductPricing(): UseProductPricingReturn {
  const { authedFetch } = useAuth()
  const [productFilter, setProductFilter] = useState<string>(PRODUCTFILTERALL)

  const paged = usePaginatedList<ProductVariantMaterialsSummaryDto, ProductFilterOption[]>({
    deps: [productFilter],
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        let url = `${APIROUTES.PRODUCTPRICING}?page=${page}&pageSize=${pageSize}`
        if (productFilter !== PRODUCTFILTERALL) {
          url += `&productId=${encodeURIComponent(productFilter)}`
        }
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return { items: body.data.items, total: body.data.total, extras: body.data.products }
      },
      [authedFetch, productFilter]
    )
  })

  const fetchDetail = useCallback(
    async (variantId: string): Promise<
      { ok: true; data: ProductVariantMaterialsDetailDto } | { ok: false; message: string }
    > => {
      const url = fillPath(APIROUTES.PRODUCTPRICINGITEM, { id: variantId })
      const res = await authedFetch(url)
      if (!res.ok) {
        const { message } = await parseApiError(res)
        return { ok: false, message }
      }
      const body = (await res.json()) as DetailBody
      return { ok: true, data: body.data }
    },
    [authedFetch]
  )

  const syncMaterials = useCallback(
    async (variantId: string, input: SyncMaterialsInput) => {
      const r = await mutateJson<ProductVariantMaterialsDetailDto>(
        authedFetch,
        fillPath(APIROUTES.PRODUCTPRICINGITEM, { id: variantId }),
        'PATCH',
        input
      )
      if (!r.ok) return r
      // Update the summary row from the detail payload so material_count
      // refreshes in the list without a round trip.
      paged.updateItem(
        (it) => it.variant_id === variantId,
        {
          variant_id: r.data.variant_id,
          product_id: r.data.product_id,
          product_name: r.data.product_name,
          axes_label: r.data.axes_label,
          baseline_cost_paisa: r.data.baseline_cost_paisa,
          preview_baseline_cost_paisa: r.data.preview_baseline_cost_paisa,
          material_count: r.data.current_links.length,
          source_type: r.data.source_type
        }
      )
      return { ok: true as const, data: r.data }
    },
    [authedFetch, paged]
  )

  const savePreviewCost = useCallback(
    async (variantId: string, previewCostPaisa: number) => {
      const r = await mutateJson<ProductVariantMaterialsDetailDto>(
        authedFetch,
        fillPath(APIROUTES.PRODUCTPRICINGPREVIEWCOST, { id: variantId }),
        'PATCH',
        { preview_baseline_cost_paisa: previewCostPaisa }
      )
      if (!r.ok) return r
      // Refresh the summary row's preview from the detail so the list +
      // the Accept-enable state update without a round trip.
      paged.updateItem(
        (it) => it.variant_id === variantId,
        {
          variant_id: r.data.variant_id,
          product_id: r.data.product_id,
          product_name: r.data.product_name,
          axes_label: r.data.axes_label,
          baseline_cost_paisa: r.data.baseline_cost_paisa,
          preview_baseline_cost_paisa: r.data.preview_baseline_cost_paisa,
          material_count: r.data.current_links.length,
          source_type: r.data.source_type
        }
      )
      return { ok: true as const, data: r.data }
    },
    [authedFetch, paged]
  )

  const acceptVariant = useCallback(
    async (variantId: string): Promise<{ ok: true } | { ok: false; message: string }> => {
      const url = fillPath(APIROUTES.PRODUCTPRICINGACCEPT, { id: variantId })
      const res = await authedFetch(url, { method: 'POST' })
      if (!res.ok) {
        const { message } = await parseApiError(res)
        return { ok: false, message }
      }
      return { ok: true }
    },
    [authedFetch]
  )

  const cloneMaterials = useCallback(
    async (
      sourceVariantId: string
    ): Promise<{ ok: true; clonedCount: number } | { ok: false; message: string }> => {
      const url = fillPath(APIROUTES.PRODUCTPRICINGCLONE, { id: sourceVariantId })
      const res = await authedFetch(url, { method: 'POST' })
      if (!res.ok) {
        const { message } = await parseApiError(res)
        return { ok: false, message }
      }
      const body = (await res.json()) as { success: true; data: { cloned_count: number } }
      return { ok: true, clonedCount: body.data.cloned_count }
    },
    [authedFetch]
  )

  const listProductVariants = useCallback(
    async (
      productId: string
    ): Promise<{ ok: true; variants: ProductVariantMaterialsSummaryDto[] } | { ok: false; message: string }> => {
      const url = `${APIROUTES.PRODUCTPRICING}?page=1&pageSize=200&productId=${encodeURIComponent(productId)}`
      const res = await authedFetch(url)
      if (!res.ok) {
        const { message } = await parseApiError(res)
        return { ok: false, message }
      }
      const body = (await res.json()) as ListBody
      return { ok: true, variants: body.data.items }
    },
    [authedFetch]
  )

  return {
    items: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    fetchDetail,
    syncMaterials,
    savePreviewCost,
    acceptVariant,
    cloneMaterials,
    listProductVariants,
    products: paged.extras ?? [],
    productFilter,
    setProductFilter
  }
}
